import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { FolderInput } from 'lucide-react';
import { sessionApi } from '@/services/api';

interface MoveSessionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionName: string;
  currentFolderId: string | null;
  onMove: (newFolderId: string | null) => Promise<void>;
}

export function MoveSessionModal({
  open,
  onOpenChange,
  sessionName,
  currentFolderId,
  onMove,
}: MoveSessionModalProps) {
  const [folders, setFolders] = useState<Array<{ folder_id: string; folder_name: string }>>([]);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(currentFolderId);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (open) {
      loadFolders();
      setSelectedFolder(currentFolderId);
      setError('');
    }
  }, [open, currentFolderId]);

  const loadFolders = async () => {
    try {
      const folderList = await sessionApi.fetchFolders();
      setFolders(folderList);
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  const handleMove = async () => {
    // Check if the folder has actually changed
    if (selectedFolder === currentFolderId) {
      setError('Please select a different folder.');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      await onMove(selectedFolder);
      onOpenChange(false);
    } catch (err: any) {
      console.error('Failed to move session:', err);
      setError(err?.message || 'Failed to move session. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center flex-shrink-0">
              <FolderInput className="w-5 h-5 text-yellow-600" />
            </div>
            <div>
              <DialogTitle>Move Session</DialogTitle>
              <DialogDescription>
                Move "{sessionName}" to a different folder.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="folder-select">Select Folder</Label>
            <Select
              value={selectedFolder || 'none'}
              onValueChange={(value) => setSelectedFolder(value === 'none' ? null : value)}
            >
              <SelectTrigger className="flex-1">
                <SelectValue>
                  {selectedFolder
                    ? folders.find(f => f.folder_id === selectedFolder)?.folder_name || 'Select folder'
                    : 'No folder'}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No folder</SelectItem>
                {folders.map((folder) => (
                  <SelectItem key={folder.folder_id} value={folder.folder_id}>
                    {folder.folder_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg border border-red-200">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleMove} disabled={isLoading}>
            {isLoading ? 'Moving...' : 'Move'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
